import { FC, ReactElement, useState } from 'react';
import * as Yup from 'yup';
import i18next from 'i18next';
import { Alert, Button, Form, Spinner, Stack } from 'react-bootstrap';
import { Formik, FormikHelpers, FormikProps, getIn } from 'formik';
import { HttpMethod, useAuthFetchFromRestAPI } from '../../util/api';
import { PromMetric, PromQuestionType, PromTriggerType } from '../../../../shared/utils/enums';
import { PromQuestionDto, PromQuestionLocalizedContentDto } from '../../../../shared/dtos/prom.dto';
import { PromQuestionLocalizedContentRequest, PromQuestionRequest } from '../../../../shared/requests/prom.request';
import { getSupportedLanguageCodes } from '../../util/prom-utils';
import { resolveErrorMessage } from '../../util/error-utils';

type LocalizedFormValues = {
  languageCode: string;
  title: string;
  questionText: string;
  options: string;
  rangeMin: string;
  rangeMax: string;
};

type FormValues = {
  questionType: PromQuestionType;
  trigger: PromTriggerType;
  metric: string;
  isActive: boolean;
  localizedContent: LocalizedFormValues[];
};

type PromQuestionFormProps = {
  initialData?: PromQuestionDto;
  onClose: () => void;
  onSubmit: (data: PromQuestionDto) => void;
};

type FieldValidation = {
  isInvalid: boolean;
  message: string | null;
};

const optionalText = (): Yup.StringSchema<string> => Yup.string().defined().ensure();

const splitOptions = (value: string): string[] => {
  return value.split('\n').map((option: string) => option.trim()).filter((option: string) => option !== '');
};

const buildInitialLocalizedContent = (initialData?: PromQuestionDto): LocalizedFormValues[] => {
  return getSupportedLanguageCodes().map((languageCode: string) => {
    const content: PromQuestionLocalizedContentDto | undefined = initialData?.localizeContent.find(
      (entry: PromQuestionLocalizedContentDto) => entry.languageCode === languageCode,
    );

    return {
      languageCode,
      title: content?.title ?? '',
      questionText: content?.questionText ?? '',
      options: (content?.options ?? []).join('\n'),
      rangeMin: content?.rangeLabels?.min ?? '',
      rangeMax: content?.rangeLabels?.max ?? '',
    };
  });
};

const PromQuestionForm: FC<PromQuestionFormProps> = ({
  initialData,
  onSubmit,
  onClose
}: PromQuestionFormProps): ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { authFetch } = useAuthFetchFromRestAPI();

  const initialValues: FormValues = {
    questionType: initialData?.questionType ?? PromQuestionType.RANGE,
    trigger: initialData?.trigger ?? PromTriggerType.AFTER_EVERY_TRAINING,
    metric: initialData?.metric ?? '',
    isActive: initialData?.isActive ?? true,
    localizedContent: buildInitialLocalizedContent(initialData),
  };

  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    questionType: Yup.mixed<PromQuestionType>().oneOf(Object.values(PromQuestionType)).required(),
    trigger: Yup.mixed<PromTriggerType>().oneOf(Object.values(PromTriggerType)).required(),
    metric: optionalText(),
    isActive: Yup.boolean().required(),
    localizedContent: Yup.array()
      .of(
        Yup.object().shape({
          languageCode: Yup.string().required(),
          title: Yup.string().trim().required(i18next.t('generic.required')),
          questionText: Yup.string().trim().required(i18next.t('generic.required')),
          options: optionalText().when('$questionType', {
            is: (questionType: PromQuestionType) => questionType === PromQuestionType.MULTIPLE,
            then: (schema: Yup.StringSchema<string>) => schema.test(
              'has-options',
              i18next.t('generic.required'),
              (value: string) => splitOptions(value).length > 0,
            ),
          }),
          rangeMin: optionalText().when('$questionType', {
            is: (questionType: PromQuestionType) => questionType === PromQuestionType.RANGE,
            then: (schema: Yup.StringSchema<string>) => schema.trim().required(i18next.t('generic.required')),
          }),
          rangeMax: optionalText().when('$questionType', {
            is: (questionType: PromQuestionType) => questionType === PromQuestionType.RANGE,
            then: (schema: Yup.StringSchema<string>) => schema.trim().required(i18next.t('generic.required')),
          }),
        }),
      )
      .required(),
  });

  const createInput = (values: FormValues): PromQuestionRequest => {
    const isRange: boolean = values.questionType === PromQuestionType.RANGE;
    const isMultiple: boolean = values.questionType === PromQuestionType.MULTIPLE;

    const localizedContent: PromQuestionLocalizedContentRequest[] = values.localizedContent.map(
      (content: LocalizedFormValues) => ({
        languageCode: content.languageCode,
        title: content.title.trim(),
        questionText: content.questionText.trim(),
        options: isMultiple ? splitOptions(content.options) : [],
        rangeLabels: isRange
          ? { min: content.rangeMin.trim(), max: content.rangeMax.trim() }
          : undefined,
      }),
    );

    const hasMetric: boolean = values.trigger === PromTriggerType.AFTER_ENDURANCE_TRAINING && values.metric !== '';

    return {
      localizedContent,
      questionType: values.questionType,
      trigger: values.trigger,
      isActive: values.isActive,
      metric: hasMetric ? values.metric : undefined,
    };
  };

  const submitData = async (payload: PromQuestionRequest): Promise<PromQuestionDto> => {
    const url: string = initialData
      ? `/api/v1/promquestions/${encodeURIComponent(initialData.id)}`
      : '/api/v1/promquestions';

    const method: HttpMethod = initialData ? 'PUT' : 'POST';

    return authFetch(url, { method: method, body: payload });
  };

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);

    try {
      const result: PromQuestionDto = await submitData(createInput(values));
      onSubmit(result);
    } catch (error) {
      console.error(error);
      setErrorMessage(resolveErrorMessage(error));
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const renderLocalizedFields = (index: number, props: FormikProps<FormValues>): ReactElement => {
    const { values, errors, touched, handleChange, handleBlur } = props;
    const content: LocalizedFormValues = values.localizedContent[index];

    const validateField = (field: keyof LocalizedFormValues): FieldValidation => {
      const path: string = `localizedContent[${index}].${field}`;
      const message: string | null = getIn(errors, path) ?? null;
      const isTouched: boolean = getIn(touched, path) === true;

      return { isInvalid: isTouched && message !== null, message };
    };

    const title: FieldValidation = validateField('title');
    const questionText: FieldValidation = validateField('questionText');
    const rangeMin: FieldValidation = validateField('rangeMin');
    const rangeMax: FieldValidation = validateField('rangeMax');
    const options: FieldValidation = validateField('options');

    return (
      <div key={content.languageCode} className="border rounded-3 p-3">
        <p className="fw-semibold mb-2">{content.languageCode.toUpperCase()}</p>

        <Form.Group className="mb-3" controlId={`title-${content.languageCode}`}>
          <Form.Label>{i18next.t('promQuestion.title')}</Form.Label>
          <Form.Control
            type="text"
            name={`localizedContent[${index}].title`}
            value={content.title}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={title.isInvalid}
          />
          <Form.Control.Feedback type="invalid">
            {title.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId={`questionText-${content.languageCode}`}>
          <Form.Label>{i18next.t('promQuestion.questionText')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            name={`localizedContent[${index}].questionText`}
            value={content.questionText}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={questionText.isInvalid}
          />
          <Form.Control.Feedback type="invalid">
            {questionText.message}
          </Form.Control.Feedback>
        </Form.Group>

        {values.questionType === PromQuestionType.RANGE && (
          <>
            <Form.Group className="mb-3" controlId={`rangeMin-${content.languageCode}`}>
              <Form.Label>{i18next.t('promQuestion.rangeMin')}</Form.Label>
              <Form.Control
                type="text"
                name={`localizedContent[${index}].rangeMin`}
                value={content.rangeMin}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={rangeMin.isInvalid}
              />
              <Form.Control.Feedback type="invalid">
                {rangeMin.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId={`rangeMax-${content.languageCode}`}>
              <Form.Label>{i18next.t('promQuestion.rangeMax')}</Form.Label>
              <Form.Control
                type="text"
                name={`localizedContent[${index}].rangeMax`}
                value={content.rangeMax}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={rangeMax.isInvalid}
              />
              <Form.Control.Feedback type="invalid">
                {rangeMax.message}
              </Form.Control.Feedback>
            </Form.Group>
          </>
        )}

        {values.questionType === PromQuestionType.MULTIPLE && (
          <Form.Group controlId={`options-${content.languageCode}`}>
            <Form.Label>{i18next.t('promQuestion.options')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name={`localizedContent[${index}].options`}
              value={content.options}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={options.isInvalid}
            />
            <Form.Control.Feedback type="invalid">
              {options.message}
            </Form.Control.Feedback>
            <div className="form-text">{i18next.t('promQuestion.optionsHint')}</div>
          </Form.Group>
        )}
      </div>
    );
  };

  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {(props: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={props.handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1 }}>
            <Stack gap={3}>
              <Form.Group controlId="questionType">
                <Form.Label>{i18next.t('promQuestion.questionType')}</Form.Label>
                <Form.Select
                  name="questionType"
                  className="select-xl"
                  value={props.values.questionType}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                >
                  {Object.values(PromQuestionType).map((questionType: PromQuestionType) => (
                    <option key={questionType} value={questionType}>
                      {i18next.t(`promQuestion.questionTypes.${questionType}`)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="trigger">
                <Form.Label>{i18next.t('promQuestion.trigger')}</Form.Label>
                <Form.Select
                  name="trigger"
                  className="select-xl"
                  value={props.values.trigger}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                >
                  {Object.values(PromTriggerType).map((trigger: PromTriggerType) => (
                    <option key={trigger} value={trigger}>
                      {i18next.t(`promQuestionnaireDetails.trigger.${trigger}`)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {props.values.trigger === PromTriggerType.AFTER_ENDURANCE_TRAINING && (
                <Form.Group controlId="metric">
                  <Form.Label>{i18next.t('promQuestion.metric')}</Form.Label>
                  <Form.Select
                    name="metric"
                    className="select-xl"
                    value={props.values.metric}
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                  >
                    <option value="">{i18next.t('promQuestion.noMetric')}</option>
                    {Object.values(PromMetric).map((metric: PromMetric) => (
                      <option key={metric} value={metric}>
                        {i18next.t(`enduranceTrainingType.promMetric.${metric}`)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              <Form.Group controlId="isActive">
                <Form.Check
                  type="checkbox"
                  name="isActive"
                  label={i18next.t('promQuestion.isActive')}
                  checked={props.values.isActive}
                  onChange={props.handleChange}
                />
              </Form.Group>

              {props.values.localizedContent.map((_: LocalizedFormValues, index: number) =>
                renderLocalizedFields(index, props),
              )}

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={props.isSubmitting || isSaving}>
                  {i18next.t('generic.close')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={props.isSubmitting || isSaving}>
                  {i18next.t(initialData ? 'generic.save' : 'generic.create')}
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PromQuestionForm;
